import { stringify } from "qs";
import { get } from "lodash";
import axios from "axios";

import subrequests, {
  getHeaders,
  getHeadersForCustomResource,
} from "./subrequests";

const mapRelationship = (relationship, included) => {
  if (relationship === null) {
    return null;
  }
  if (relationship.data === null) {
    return null;
  }
  if (Array.isArray(relationship.data)) {
    let data = relationship.data.map((item) => {
      const { attributes, relationships } = included.find(
        (include) => include.type === item.type && include.id === item.id
      ) || { attributes: {}, relationships: {} };

      return {
        ...item,
        ...attributes,
        relationships: mapRelationships(relationships, included),
      };
    });
    const sort = get(data, "[0].order_index", false);
    if (sort !== false) {
      data = data.sort((a, b) => a.order_index - b.order_index);
    }

    return data;
  } else if (typeof relationship.data === "object") {
    const { attributes, relationships } = included.find(
      (include) =>
        include.type === relationship.data.type &&
        include.id === relationship.data.id
    ) || { attributes: {}, relationships: {} };
    const remappedRelationship = {
      ...relationship.data,
      ...attributes,
      relationships: mapRelationships(relationships, included),
    };
    return remappedRelationship;
  }
};
const mapRelationships = (relationships, included) =>
  relationships
    ? Object.keys(relationships).reduce(
      (remapped, relationshipName) => ({
        ...remapped,
        [relationshipName]: {
          ...relationships[relationshipName],
          data: mapRelationship(relationships[relationshipName], included),
        },
      }),
      {}
    )
    : {};

const mapList = ({ data, included, meta: { count } }) => {
  const finalData = {
    data: data.map((item) =>
      get(mapOne({ data: item, included, mode: "list" }), "data")
    ),
    total: parseInt(count),
  };
  return finalData;
};

const mapOne = ({ data, included }) => {
  const finalData = {
    data: {
      id: data.id,
      ...data.attributes,
      relationships:
        data.relationships && included
          ? mapRelationships(data.relationships, included)
          : data.relationships,
    },
  };
  return finalData;
};

const createEntity = async (item, settings = {}) => {
  const apiUrl = get(settings, "apiUrl");
  const entityType = get(item, "type", null);
  if (entityType === null) {
    return Promise.reject("Invalid item type for: " + JSON.stringify(item));
  }
  const attributes = { ...item };
  delete attributes.id;
  delete attributes.status;
  delete attributes.changed;
  delete attributes.created;
  delete attributes.default_langcode;
  delete attributes.drupal_internal__id;
  delete attributes.drupal_internal__vid;
  delete attributes.langcode;
  delete attributes.relationships;
  delete attributes.revision_created;
  delete attributes.revision_log_message;
  delete attributes.revision_translation_affected;

  const response = await fetch(`${apiUrl}/${get(item, "type")}`, {
    method: "POST",
    headers: getHeaders(settings),
    body: JSON.stringify({
      data: {
        type: get(item, "type"),
        attributes,
        relationships: get(item, "relationships", {}),
      },
    }),
  });
  const { status } = response;
  if (status >= 200 && status < 300) {
    return response.json().then(({ data }) => ({
      id: data.id,
      type: get(item, "type"),
    }));
  } else {
    return Promise.resolve({
      id: null,
      type: get(item, "type"),
    });
  }
};
const updateEntity = async (item, settings = {}) => {
  const entityType = get(item, "type", null);
  const apiUrl = get(settings, "apiUrl");
  if (entityType === null) {
    return Promise.reject(`Invalid Entity Type`);
  }
  const response = await fetch(
    `${apiUrl}/${get(item, "type")}/${get(item, "id")}`,
    {
      method: "PATCH",
      headers: getHeaders(settings),
      body: JSON.stringify({
        data: {
          type: get(item, "type"),
          id: item.id,
          attributes: { ...item, relationships: undefined, type: undefined },
          relationships: get(item, "relationships"),
        },
      }),
    }
  );
  const { status } = response;
  if (status >= 200 && status < 300) {
    return Promise.resolve({
      id: item.id,
      type: get(item, "type"),
    });
  } else {
    return Promise.reject({
      id: null,
      type: get(item, "type"),
    });
  }
};

/**
 * Upload new file in to Drupal.
 * The upload feature requires RestUI plugin enabled in to the installation.
 *
 * @param {string} referenceEntity The referenced entity for which you are creating the file.
 * @param {string} type Type associated to the file to upload, necessary to create the final URL where file will be uploaded.
 * @param {File} rawFile The rawFile object to upload.
 */
const uploadFile = async (referenceEntity, type, rawFile, settings) => {
  const csrfToken = localStorage.getItem("csrf_token");
  const apiUrl = get(settings, "apiUrl");
  const baseEntityType = get(
    settings,
    `map.${referenceEntity}`,
    referenceEntity
  );
  const url = `${apiUrl}/${baseEntityType}/${type}`;
  const username = localStorage.getItem("username");
  const password = localStorage.getItem("password");
  const headers = new Headers({
    "Content-Type": "application/octet-stream",
    "Content-Disposition": `filename="${rawFile.name}"`,
    "X-CSRF-Token": csrfToken,
    Authorization: `Basic ${btoa(username + ":" + password)}`,
  });
  const formData = new FormData();
  formData.append("file", rawFile);
  const {
    data: { id },
  } = await fetch(url, {
    method: "POST",
    body: formData,
    headers: headers,
  }).then((response) => response.json());
  return Promise.resolve(id);
};

const isRelationshipReference = (item) => {
  const type = get(item, "type", null);
  const relationships = get(item, "relationships", null);
  const id = get(item, "id", null);
  return (
    type !== null &&
    id !== null &&
    Object.keys(item).length === (relationships !== null ? 3 : 2)
  );
};
const prepareDataForEntity = async (
  entity,
  relationshipName,
  settings,
  referenceEntity
) => {
  const file = get(entity, "rawFile", null);
  const type = get(entity, "type", null);
  if (type === "file--file" || (file !== null && file instanceof File)) {
    if (get(entity, "id", null) !== null) {
      // File already exists, nothing to do.
      return Promise.resolve({
        id: entity.id,
        type: entity.type,
      });
    }
    const id = await uploadFile(
      referenceEntity,
      relationshipName,
      file,
      settings
    );
    return Promise.resolve({ id, type: "file--file" });
  } else if (get(entity, "id", null) === null) {
    return await createEntity(entity, settings);
  } else {
    return await updateEntity(entity, settings);
  }
};
const prepareDataForRelationships = async (
  data,
  settings,
  referenceEntity = ""
) => {
  const relationships = get(data, "relationships", null);
  if (relationships === null) {
    // Good, nothing more to do with this item.
    return Promise.resolve({});
  }
  return Object.keys(relationships).reduce(
    async (reducedData, relationshipName) => {
      let relationshipData = get(
        relationships,
        `${relationshipName}.data`,
        null
      );
      const type = get(data, "type");
      const saveableList = get(settings, `saveable.${type}`, []);
      const saveable = saveableList.indexOf(relationshipName) !== -1;
      if (saveable && Array.isArray(relationshipData)) {
        for (var i = 0; i < relationshipData.length; i++) {
          let arrayItem = relationshipData[i];
          if (isRelationshipReference(arrayItem)) {
            continue;
          }

          let type = get(arrayItem, "type", null);
          if (type === null) {
            type = get(settings, `map.${relationshipName}`, null);
            if (type === null) {
              return Promise.reject(
                `Invalid item type for: ${relationshipName}`
              );
            }
            arrayItem.type = type;
          }

          arrayItem.relationships = await prepareDataForRelationships(
            arrayItem,
            settings,
            relationshipName
          );

          relationshipData[i] = await prepareDataForEntity(
            arrayItem,
            relationshipName,
            settings,
            referenceEntity
          );
        }
      } else if (saveable && relationshipData !== null) {
        if (!isRelationshipReference(relationshipData)) {
          let type = get(relationshipData, "type", null);
          if (type === null) {
            type = get(settings, `map.${relationshipName}`, null);
            relationshipData.type = type;
          }
          relationshipData.relationships = await prepareDataForRelationships(
            relationshipData,
            settings,
            relationshipName
          );
          relationshipData = await prepareDataForEntity(
            relationshipData,
            relationshipName,
            settings,
            referenceEntity
          );
        }
      }
      let newData = {
        ...(await reducedData),
        [relationshipName]: {
          ...relationships[relationshipName],
          data: relationshipData,
        },
      };
      return Promise.resolve(newData);
    },
    Promise.resolve({})
  );
};

const prepareData = async (data, settings) => {
  return {
    ...data,
    relationships: await prepareDataForRelationships(data, settings),
  };
};

/**
 *
 * @param {*} api  available BASE and CASA_DI_VETRO
 * @param {*} settings
 * @returns
 */
const dataProvider = (api, settings = {}) => ({
  // GET
  get: async (resource, params) => {
    const limit = 10;
    const offset = (params.page - 1) * 10 || 1;
    let url = `${api.base}/${resource}/resource?_format=json&${stringify(
      params
    )}&ts=${new Date().getTime()}&limit=${limit}&offset=${offset}`;
    const response = await fetch(url, {
      method: "GET",
      headers: getHeaders(),
    });
    const { data } = await response.json();
    return { data };
  },
  // GET POST
  getPost: async (resource, params) => {
    const { method, ...body } = params;
    const url = `${api.base}/${resource}/resource?_format=json&method=${method}&uuid=${params?.uuid}`;
    const response = await fetch(url, {
      method: "POST",
      headers: getHeaders({
        headers: {
          "Content-Type": "application/json",
        },
      }),
      body: JSON.stringify(body),
    });
    const { data } = await response.json();
    return { data };
  },
  // POST
  post: async (resource, params) => {
    const { method, body } = params;
    const url = `${api.base}/${resource}/resource?_format=json&method=${method}`;
    const response = await fetch(url, {
      method: "POST",
      headers: getHeaders({
        headers: {
          "Content-Type": "application/json",
        },
      }),
      body: JSON.stringify(body),
    });
    const { data } = await response.json();
    return { data };
  },
  // GET TREE NODE
  getTreeRoots: async (resource) => {
    const timestamp = new Date().getTime();
    const url = `${api.base}/${resource}/resource?_format=json&ts=${timestamp}&method=roots`;
    try {
      const response = await fetch(url, {
        method: "GET",
        headers: getHeaders(),
      });
      const { roots, message } = await response.json();
      return await (!roots
        ? Promise.reject(message)
        : Promise.resolve({ data: { roots } }));
    } catch (error) {
      return await Promise.reject(error);
    }
  },
  // GET TREE CHILDREN
  getTreeChildren: async (resource, id) => {
    const timestamp = new Date().getTime();
    const url = `${api.base}/${resource}/resource?_format=json&ts=${timestamp}&method=children&id=${id}`;
    const response = await fetch(url, {
      method: "GET",
      headers: getHeaders(),
    });
    const { children } = await response.json();
    return {
      data: { children },
    };
  },
  // GET TREE CHILDREN
  getTreePathinfo: async (resource, { id }) => {
    const timestamp = new Date().getTime();
    const url = `${api.base}/${resource}/resource?_format=json&ts=${timestamp}&method=pathinfo&id=${id}`;
    const response = await fetch(url, {
      method: "GET",
      headers: getHeaders(),
    });
    const { data } = await response.json();
    return { data };
  },

  postTreeMove: async (resource, id, destinationId) => {
    const url = `${api.base}/${resource}/resource?_format=json&method=move`;
    const response = await fetch(url, {
      method: "POST",
      headers: getHeadersForCustomResource(),
      body: JSON.stringify({
        id,
        destination_id: destinationId /** Respect drupal notation. */,
      }),
    });
    const { data } = await response.json();
    return { data };
  },

  solveHierarchy: async (resource) => {
    const url = `${api.base}/${resource}/resource?_format=json&method=solveHierarchy`;
    const response = await fetch(url, {
      method: "GET",
      headers: getHeaders(),
    });
    const { data } = await response.json();
    return { data };
  },

  getList: async (resource, params) => {
    const { filter = {}, pagination, sort } = params;
    const { perPage, page } = pagination;
    // Imposta valori di default se non viene fornito sort o il campo sort è vuoto
    const sortField = sort && sort.field ? sort.field : "id";
    const sortOrder = sort && sort.order ? sort.order : "DESC";
    const prefix = sortOrder === "ASC" ? "" : "-";

    // Costruisci la query base per paginazione
    let query = {
      offset: (page - 1) * perPage,
      limit: perPage,
    };

    // Aggiungi lo sort solo se il campo sort ha un valore significativo
    if (sortField) {
      query.sort = `${prefix}${sortField}`;
    }

    // Includi il parametro include solo se valorizzato
    const includeSetting = get(settings, `include.${resource}`, []);
    if (includeSetting.length > 0) {
      query.include = includeSetting.join(",");
    }

    // Aggiungi i filtri solo se sono presenti e hanno valori validi
    if (filter && Object.keys(filter).length > 0) {
      Object.keys(filter).forEach((key) => {
        // Se il valore del filtro è una stringa non vuota, aggiungilo
        if (
          filter[key] !== undefined &&
          filter[key] !== null &&
          filter[key] !== ""
        ) {
          query[`filter[${key}]`] = filter[key];
        }
      });
    }

    // Se sono stati impostati parametri custom per il resource, aggiungi il suffisso
    let suffix = "";
    if (settings.custom && settings.custom[resource]) {
      suffix = "&_format=json";
    }
    const alias = get(settings, `alias.${resource}`, resource);
    const url = `${api.base}/${alias}?${stringify(query)}${suffix}`;

    const response = await fetch(url, {
      method: "GET",
      headers: getHeaders(
        settings,
        get(settings, `custom.${resource}.auth`, true)
      ),
    });
    const result_1 = await response.json();
    return mapList(result_1);
  },

  getOne: async (resource, params) => {
    const { id } = params;
    const include = get(settings, `include.${resource}`, []).join(",");
    const url = `${api.base}/${resource}/${id}?include=${include}`;
    const response = await fetch(url, {
      method: "GET",
      headers: getHeaders(settings),
    });
    const result_1 = await response.json();
    return mapOne(result_1);
  },
  getMany: async (resource, params) => {
    let query = stringify({
      "filter[id][condition][operator]": "IN",
      "filter[id][condition][path]": "id",
    });
    params.ids.forEach(
      (id) => (query = `${query}&filter[id][condition][value][]=${id}`)
    );

    const url = `${api.base}/${resource}?${query}`;
    const response = await fetch(url, {
      method: "GET",
      headers: getHeaders(settings),
    });
    const {
      data,
      meta: { count },
    } = await response.json();
    return {
      data: data.map((item) => ({
        id: item.id,
        ...item.attributes,
        relationships: item.relationships,
      })),
      total: parseInt(count, 10),
    };
  },
  getManyReference: async (resource, params) => {
    const { id, filter, pagination, target } = params;
    const { page, perPage } = pagination;
    const query = {
      "page[number]": page,
      "page[limit]": perPage,
    };

    Object.keys(filter || {}).forEach(
      (key) => (query[`filter[${key}]`] = filter[key])
    );
    if (target && id) {
      query[`filter[${params.target}]`] = id;
    }
    const url = `${api.base}/${resource}?${stringify(query)}`;
    const response = await fetch(url, {});
    const {
      data,
      meta: { count },
    } = await response.json();
    return {
      data: data.map((item) => ({
        id: item.id,
        ...item.attributes,
        relationships: item.relationships,
      })),
      count: parseInt(count, 10),
    };
  },
  create: async (resource, params) => {
    const url = `${api.base}/${resource}`;
    const attributes = params.data;
    const relationships = attributes.relationships;
    delete attributes.relationships;
    const data = {
      type: resource,
      attributes,
      relationships,
    };

    if (get(settings, "subrequests", []).indexOf(resource) !== -1) {
      let url_1 = api.base.replace("/api", "");
      const requests = await subrequests(data, settings);
      const response = await fetch(`${url_1}/subrequests?_format=json`, {
        method: "POST",
        headers: getHeaders(),
        body: JSON.stringify(requests),
      });
      const response_1 = await response.json();
      return {
        data: get(
          JSON.parse(
            get(response_1, "last.body", get(response_1, "last#body{0}.body"))
          ),
          "data"
        ),
      };
    }

    const data_3 = await prepareData(data, settings);
    const response_2 = await fetch(url, {
      method: "POST",
      headers: getHeaders(settings),
      body: JSON.stringify({
        data,
      }),
    });
    const {
      data: { id, attributes: attributes_1, relationships: relationships_1 },
    } = await response_2.json();
    return {
      data: {
        id,
        ...attributes_1,
        relationships,
      },
    };
  },
  update: async (resource, params) => {
    const { id } = params;
    const url = `${api.base}/${resource}/${params.id}`;
    const attributes = params.data;
    const relationships = attributes.relationships;
    delete attributes.id;
    delete attributes.relationships;

    const data = {
      id,
      type: resource,
      attributes,
      relationships,
    };

    if (get(settings, "subrequests", []).indexOf(resource) !== -1) {
      let url_1 = api.base.replace("/api", "");
      const requests = await subrequests(data, settings);
      const response = await fetch(`${url_1}/subrequests?_format=json`, {
        method: "POST",
        headers: getHeadersForCustomResource(settings),
        body: JSON.stringify(requests),
      });
      const response_1 = await response.json();
      return {
        data: get(
          JSON.parse(
            get(response_1, "last.body", get(response_1, "last#body{0}.body"))
          ),
          "data"
        ),
      };
    }

    const data_3 = await prepareData(data, settings);
    const response_2 = await fetch(url, {
      method: "PATCH",
      headers: getHeaders(settings),
      body: JSON.stringify({
        data,
      }),
    });
    const {
      data: {
        id: id_1,
        attributes: attributes_1,
        relationships: relationships_1,
      },
    } = await response_2.json();
    return {
      data: {
        id,
        ...attributes_1,
        relationships,
      },
    };
  },
  updateMany: (resource, params) => Promise,
  delete: async (resource, params) => {
    const { id } = params;
    const url = `${api.base}/${resource}/${id}`;
    const response = await fetch(url, {
      method: "DELETE",
      headers: getHeaders(settings),
    });
    if (response.status < 200 || response.status > 300) {
      return Promise.reject(response.statusText);
    }
    return await Promise.resolve({ error: false, data: { id } });
  },
  deleteMany: async (resource, params) => {
    const { ids } = params;
    const responses = await Promise.all(
      ids.map((id) =>
        fetch(`${api.base}/${resource}/${id}`, {
          method: "DELETE",
          headers: getHeaders(settings),
        }).then(({ status, statusText }) => ({
          error: status < 200 || status > 300,
          message: statusText,
        }))
      )
    );
    let errors = responses.filter((r) => r.error);
    if (errors.length > 0) {
      return Promise.reject(errors.map((e) => e.message).join("\n"));
    }
    return {
      data: { ids },
    };
  },


  getPdf: async (resource, params) => {
    const axiosCasaDiVetroInstance = axios.create({
      baseURL: api.casaDiVetro,
      headers: getHeaders,
      responseType: 'blob'
    });

    try {

      const queryString = Object.entries(params)
        .filter(([_, value]) => value !== "" && value !== null)
        .map(([key, value]) => `${encodeURIComponent(key)}=${encodeURIComponent(value)}`)
        .join("&");

      const response = await axiosCasaDiVetroInstance.get(
        `/${resource}${queryString ? `?${queryString}` : ''}`
      );

      return {
        data: response.data,
      };
    } catch (error) {
      console.error("Error fetching data:", error);
      return { data: [] };
    }
  },

  // *****************************
  // * CASA DI VETRO *
  // *****************************

  getCasaDiVetroList: async (resource, params) => {
    const axiosCasaDiVetroInstance = axios.create({
      baseURL: api.casaDiVetro,
      headers: getHeaders,
    });

    try {

      const queryString = Object.entries(params)
        .filter(([_, value]) => value !== "" && value !== null)
        .map(([key, value]) => `${encodeURIComponent(key)}=${encodeURIComponent(value)}`)
        .join("&");

      const response = await axiosCasaDiVetroInstance.get(
        `/${resource}${queryString ? `?${queryString}` : ''}`
      );

      return {
        data: response.data,
      };
    } catch (error) {
      console.error("Error fetching data:", error);
      return { data: [] };
    }
  }

  // *****************************
  // * END CASA DI VETRO *
  // *****************************
});
export default dataProvider;
